import React, { useCallback, useState, useMemo } from 'react';
import useSWR from 'swr';
import { t } from 'ttag';

import { Button, Heading, Text } from '@components';
import { Modal } from 'components';
import { getCookie } from '@utils';

import { useIsAuthenticated } from 'hooks';

import LoginModal from 'js/Account/Login/Modals/Modal';

import Badge from './assets/Badge.png';
import Benefits from './assets/Benefits.png';
import EngangeDesktop from './assets/Engage-Desktop.jpg';
import EngageMobile from './assets/Engage-Mobile.jpg';
import EngageTablet from './assets/Engage-Tablet.jpg';
import JerseyLab from './assets/JerseyLab.png';

/*
 * /!\ Important.
 * The Competitions React Application is currently only a single competition named "The Dream Week".
 * It is planned to be extended to support multiple competitions in the future.
 */

const fetcher = url => fetch(url).then(r => r.json());

export default function Competitions() {
  const [showLogin, toggleLogin] = useState(false);
  const [showConfirmation, toggleConfirmation] = useState(false);

  const languageCode = useMemo(() => window?.config?.language_code || 'en', []);

  const termLinks = useMemo(
    () => ({
      en: 'https://www.unisportstore.com/blog/10545-competition-rules/',
      da: 'https://www.unisport.dk/blog/10533-konkurrencebetingelser/',
      se: 'https://www.unisportstore.se/blog/10574-tavlingsregler/',
      nb: 'https://www.unisportstore.no/blog/10580-konkurranseregler/',
      fi: 'https://www.unisportstore.fi/blog/10597-kilpailujen-saannot/',
      nl: 'https://www.unisportstore.nl/blog/10552-competitieregels/',
      de: 'https://www.unisportstore.de/blog/10575-regeln-fur-wettbewerbe/',
      fr: 'https://www.unisportstore.fr/blog/10562-regles-pour-les-concours/',
      at: 'https://www.unisportstore.at/blog/10575-regeln-fur-wettbewerbe/'
    }),
    [languageCode]
  );

  const { data: prizesData, mutate } = useSWR(
    '/api/prizes/?online=True',
    fetcher
  );

  const { isAuthenticated } = useIsAuthenticated();

  const handleParticipate = useCallback(
    async prizeId => {
      if (!isAuthenticated) {
        toggleLogin(true);
      } else {
        const response = await fetch(`/api/prizes/${prizeId}/participate/`, {
          method: 'POST',
          headers: {
            'X-CSRFToken': getCookie('csrftoken')
          }
        });
        if (response.ok) {
          await mutate();
          toggleConfirmation(true);
        }
      }
    },
    [isAuthenticated]
  );

  const getButtonText = useCallback(attributes => {
    if (attributes?.status === 'Inactive' && !attributes?.is_participating) {
      return t`Expired`;
    }
    if (attributes?.is_participating) {
      return t`Joined`;
    }
    return t`Participate`;
  }, []);

  return (
    <>
      <style type="text/css">
        {`
          .engage {
            background-image: url(${EngageMobile});
          }
          
          @media (min-width: 640px) {
            .engage {
              background-image: url(${EngageTablet});
            }
          }
          
          @media (min-width: 1024px) {
            .engage {
              background-image: url(${EngangeDesktop});
            }
          }
        `}
      </style>
      <main className="min-h-screen space-y-16 bg-whisper pb-32 md:space-y-32">
        <section className="engage h-[672px] bg-dark-gray bg-cover bg-center">
          <div className="container relative z-10 flex h-full flex-col justify-center">
            <img
              src={Badge}
              alt=""
              className="absolute right-4 top-4 h-12 w-12 md:h-20 md:w-20"
            />
            <div className="max-w-screen-md space-y-4 md:space-y-8">
              <Heading as="h1" className="text-white">
                {t`The Dream Week`}
              </Heading>
              <div className="space-y-2 md:space-y-4">
                <Text className="font-semibold leading-relaxed text-white">
                  {t`8 days made for Unisport FC members.`}
                </Text>
                <Text className="font-semibold leading-relaxed text-white">
                  {t`Join us for free today to get exclusive rewards & access.`}
                </Text>
              </div>
              {!isAuthenticated && (
                <Button size="md" onClick={() => toggleLogin(true)}>
                  <Button.Text>{t`Join Unisport FC`}</Button.Text>
                </Button>
              )}
            </div>
          </div>
        </section>
        <section className="container space-y-8 md:space-y-16">
          <div className="max-w-screen-md space-y-2">
            <Heading
              as="h2"
              className="text-[40px] font-extrabold uppercase tracking-[-1px] md:text-[56px]"
            >{t`8 Days of Celebration`}</Heading>
            <Text className="font-semibold leading-relaxed text-black">
              {t`Join Unisport FC and make your football dreams come true! Dive into our Celebration Week and win incredible prizes every day during Dream Week. 🎉 Check back daily and discover what we've got in store for you!`}
            </Text>
            <a
              href={termLinks[languageCode]}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-block text-black underline"
            >{t`Read Unisport's Competition Terms`}</a>
          </div>
          <div className="grid grid-cols-1 gap-16 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {prizesData?.data
              ?.sort(
                (a, b) =>
                  new Date(a.attributes.valid_from) -
                  new Date(b.attributes.valid_from)
              )
              ?.map(({ id, attributes }) => (
                <div key={id} className="space-y-4">
                  <div className="aspect-h-5 aspect-w-4 relative w-full">
                    {['Inactive', 'Upcoming'].includes(attributes?.status) &&
                      !attributes.is_participating && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          className="absolute left-1/2 top-1/2 z-10 box-content h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-white p-4 md:h-6 md:w-6 md:p-2"
                        >
                          <path d="M19 10H20C20.5523 10 21 10.4477 21 11V21C21 21.5523 20.5523 22 20 22H4C3.44772 22 3 21.5523 3 21V11C3 10.4477 3.44772 10 4 10H5V9C5 5.13401 8.13401 2 12 2C15.866 2 19 5.13401 19 9V10ZM17 10V9C17 6.23858 14.7614 4 12 4C9.23858 4 7 6.23858 7 9V10H17ZM11 14V18H13V14H11Z" />
                        </svg>
                      )}
                    {attributes.is_participating && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        className="absolute left-1/2 top-1/2 z-10 box-content h-10 w-10 -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-gradient-to-r from-green to-[#E0F65A] p-4 md:h-6 md:w-6 md:p-2"
                      >
                        <path d="M10.0007 15.1709L19.1931 5.97852L20.6073 7.39273L10.0007 17.9993L3.63672 11.6354L5.05093 10.2212L10.0007 15.1709Z" />
                      </svg>
                    )}
                    <img
                      src={attributes?.image}
                      alt=""
                      className="h-full w-full object-cover object-center"
                    />
                  </div>
                  <span className="block text-center text-sm font-bold uppercase tracking-wide text-dark-gray">
                    {attributes?.formated_date}
                  </span>
                  {['Inactive', 'Active'].includes(attributes?.status) && (
                    <>
                      <div className="space-y-2">
                        <Heading as="h3" className="text-xl md:text-xl">
                          {attributes?.title}
                        </Heading>
                        <Text>{attributes?.description}</Text>
                      </div>
                      <Button
                        onClick={() => handleParticipate(id)}
                        disabled={
                          attributes?.status === 'Inactive' ||
                          attributes?.is_participating
                        }
                      >
                        <Button.Text>{getButtonText(attributes)}</Button.Text>
                      </Button>
                    </>
                  )}
                </div>
              ))}
          </div>
        </section>
        <section className="container space-y-8 md:space-y-16">
          <Heading
            as="h2"
            className="break-words text-[40px] font-extrabold uppercase tracking-[-1px] md:text-[56px]"
          >{t`Member Benefits`}</Heading>
          <div className="grid grid-cols-6 gap-4">
            <div className="col-span-6 md:col-span-2">
              <div className="aspect-h-5 aspect-w-4 w-full">
                <img src={Benefits} alt="" className="h-full w-full" />
              </div>
            </div>
            <div className="col-span-6 space-y-4 self-center md:col-span-3 md:col-start-4">
              <div className="space-y-2">
                <Heading as="h3">{t`Exclusive for Members`}</Heading>
                <Text className="leading-relaxed">{t`This week is all about our Unisport FC members and celebrating the football community. All year round you can get unique member benefits, exclusive products, giveaways and more of what make football dreams come true. 🏆`}</Text>
                <ul className="m-0 list-none space-y-2 p-0">
                  <li className="before:mr-2 before:content-['—']">{t`Free Print on Boots: Make it yours with name, number, emoji & flag`}</li>
                  <li className="before:mr-2 before:content-['—']">{t`Win Tickets to Amazing Football Matches & more`}</li>
                  <li className="before:mr-2 before:content-['—']">{t`Participate in Exclusive Events & Giveaways`}</li>
                  <li className="before:mr-2 before:content-['—']">{t`Jersey Lab: Design your own football shirt`}</li>
                  <li className="before:mr-2 before:content-['—']">{t`Access to Exclusive Member Products`}</li>
                  <li className="before:mr-2 before:content-['—']">{t`Create Wishlists with Your Favourite Products`}</li>
                  <li className="before:mr-2 before:content-['—']">{t`Track Orders & View Previous Purchases`}</li>
                </ul>
              </div>
              {!isAuthenticated && (
                <Button size="md" onClick={() => toggleLogin(true)}>
                  <Button.Text>{t`Join Unisport FC`}</Button.Text>
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-6 gap-4">
            <div className="order-2 col-span-6 space-y-4 self-center md:order-1 md:col-span-3">
              <div className="space-y-2">
                <Heading as="h3">{t`Jersey Lab`}</Heading>
                <Text className="leading-relaxed">
                  {t`Unleash your creativity at our Jersey Lab and design your own football shirt! With a vast selection of awesome badges, fonts, and more, you're in control. Add YOUR personal touch, create jaw-dropping designs, and show the world what you represent. Dive in now, the sky's the limit! 🎨`}
                </Text>
              </div>
              <Button size="md" as="a" href="/jersey-lab/">
                <Button.Text>{t`Try the Jersey Lab`}</Button.Text>
              </Button>
            </div>
            <div className="order-1 col-span-6 md:order-2 md:col-span-2 md:col-start-5">
              <div className="aspect-h-5 aspect-w-4 w-full">
                <img src={JerseyLab} alt="" className="h-full w-full" />
              </div>
            </div>
          </div>
        </section>
        <LoginModal
          isOpen={showLogin}
          onClose={() => {
            toggleLogin(false);
          }}
          modal="login"
          loginHeading={t`Join Unisport FC to Participate in The Dream Week`}
        />
        <Modal
          isOpen={showConfirmation}
          onClose={() => {
            toggleConfirmation(false);
          }}
          title={t`Thank you for participating!`}
          maxWidth="md"
        >
          <div className="space-y-2">
            <Text>
              {t`Thank you for being a Unisport FC member! You are now participating in the competition. The winner will be randomly picked from all entries and contacted directly by e-mail by 09.10.2024.`}
            </Text>
            <a
              href={termLinks[languageCode]}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-block text-black underline"
            >{t`Read Unisport's Competition Terms`}</a>
          </div>
        </Modal>
      </main>
    </>
  );
}
